'use client';

import { useEffect, useRef, useState } from 'react';

import { cn } from 'cn';
import { useTranslations } from 'use-intl';

import {
  SurveyThemeProvider,
  supportsDisplayMedia,
  useIsDesktop,
} from '@/shared-utilities';
import {
  InitiateSurveyV3Mutation,
  type GetProjectQuery,
} from '@/survey-graphql';

import ProjectExpired from './project-response-expired';
import {
  MIN_USABILITY_VIEWPORT_HEIGHT,
  USABILITY_SPEECH_PROMPT_MESSAGE_ID,
} from './project-response.consts';
import ProjectUsabilityCameraBubble from './project-usability-camera-bubble';
import { subscribeRecordingStart } from './project-usability-recording-state.helpers';
import { useUsabilitySidebarState } from './project-usability-recording.hooks';
import { UsabilityResponseContext } from './project-usability-response-context';
import ProjectUsabilityResponseMainFrame from './project-usability-response-main-frame';
import ProjectUsabilityResponseMobileModal from './project-usability-response-mobile-modal';
import ProjectUsabilityResponseOverlays from './project-usability-response-overlays';
import ProjectUsabilityResponsePermissionDenied from './project-usability-response-permission-denied';
import { useUsabilityResponseSession } from './project-usability-response-session.hooks';
import ProjectUsabilityResponseSidebar from './project-usability-response-sidebar';
import { useUsabilityResponseStage } from './project-usability-response-stage.hooks';
import ProjectUsabilityResponseUnsupportedBrowser from './project-usability-response-unsupported-browser';
import { useUsabilityResponseVoice } from './project-usability-response-voice.hooks';
import ProjectUsabilityResponseWelcomeModal from './project-usability-response-welcome-modal';
import { useWaitForConversationEnd } from './project-usability-wait-for-conversation-end.hooks';

// Module-level guard to prevent double recording prompt due to remounts (e.g. HMR, iframe reloads, etc.)
let globalHasStarted = false;
let globalHasEnded = false;

interface Props {
  project: GetProjectQuery['project'];
  orgSettings?: GetProjectQuery['projectOrgSettings'];
  firstQuestion: InitiateSurveyV3Mutation['initiateSurveyV3']['firstQuestion'];
  sessionId: string;
}

export default function ProjectUsabilityResponse({
  project,
  orgSettings,
  sessionId,
  firstQuestion,
}: Props) {
  const t = useTranslations('Usability');
  const tVoice = useTranslations('Voice');
  const { isDesktop, mounted } = useIsDesktop(
    1100,
    MIN_USABILITY_VIEWPORT_HEIGHT
  );

  const isPromptPlaying = useRef(false);

  const [showWelcomePrompt, setShowWelcomePrompt] = useState(true);
  const [hasRequestedStart, setHasRequestedStart] = useState(false);
  const [conversationOverText, setConversationOverText] = useState<
    string | null
  >(null);

  const session = useUsabilityResponseSession({
    project,
    sessionId,
    firstQuestion,
  });
  const {
    isMobileMode,
    isPublished,
    isSaving,
    isSubmitting,
    messages,
    insightLoading,
    isConversationOver,
    effectiveIsConversationOver,
    handleConversationOver,
    insightId,
    conversationOverRef,
    activityStore,
    getSpeech,
    speechAudios,
    isRecording,
    isPaused,
    isPausedByTimeout,
    isPermissionDenied,
    isRequestingPermission,
    recordingError,
    startRecording,
    stopRecording,
    endRecording,
    hasUrlOrTask,
  } = session;

  const { isOpen: isSidebarOpen } = useUsabilitySidebarState();

  const voice = useUsabilityResponseVoice({
    project,
    isConversationOver,
    isRecording,
    isPaused,
    isPausedByTimeout,
    isSubmitting,
  });
  const { showPrompt, currentPlayingId } = voice;

  useEffect(() => {
    void getSpeech(
      USABILITY_SPEECH_PROMPT_MESSAGE_ID,
      tVoice('speakOutLoudTts'),
      true,
      true
    );
  }, [getSpeech, tVoice]);

  useEffect(() => {
    if (isConversationOver && conversationOverRef.current) {
      setConversationOverText(conversationOverRef.current);
    }
  }, [isConversationOver, conversationOverRef]);

  useEffect(() => {
    if (!showPrompt) {
      return;
    }

    setTimeout(() => {
      const speechAudio = speechAudios.find(
        (s) => s.messageId === USABILITY_SPEECH_PROMPT_MESSAGE_ID
      );
      if (speechAudio && !isPromptPlaying.current && !currentPlayingId) {
        isPromptPlaying.current = true;
        const audio = new Audio(speechAudio.audioUrl ?? '');
        void audio.play();
        const handleEnded = () => {
          isPromptPlaying.current = false;
        };
        const handleError = () => {
          isPromptPlaying.current = false;
        };
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('error', handleError);

        return;
      }
    }, 200);
  }, [showPrompt, speechAudios, currentPlayingId]);

  const stage = useUsabilityResponseStage({
    project,
    orgSettings,
    conversationOverText,
    isSaving,
    isSubmitting,
    isConversationOver,
    effectiveIsConversationOver,
    messages,
    insightLoading,
    handleConversationOver,
    activityStore,
    insightId,
  });

  const themeValue = {
    themeSettings: project?.themeSettings,
    orgSettings: orgSettings ?? undefined,
  };

  // Handle user-initiated recording start (Firefox requires this). Note the
  // welcome modal is NOT dismissed here: we let the effect below hide it only
  // once recording actually starts, so users can't proceed while the browser's
  // mic/screen permission prompt is still pending.
  const handleStartRecording = () => {
    if (!globalHasStarted) {
      globalHasStarted = true;
      globalHasEnded = false;
      setHasRequestedStart(true);
      if (hasUrlOrTask) {
        void startRecording();
      } else {
        setShowWelcomePrompt(false);
      }
    }
  };

  // Dismiss the welcome modal only once recording has actually started, so it
  // stays up while the browser's screen/mic permission prompt is still pending.
  useEffect(
    () => subscribeRecordingStart(() => setShowWelcomePrompt(false)),
    []
  );

  useEffect(() => {
    const handleUnload = () => {
      stopRecording();
      globalHasStarted = false;
      globalHasEnded = false;
    };
    window.addEventListener('unload', handleUnload);
    return () => {
      stopRecording();
      window.removeEventListener('unload', handleUnload);
    };
  }, [stopRecording]);

  // Waits for the final TTS line to finish before stopping the recording,
  // instead of cutting audio off mid-sentence on the "thanks, we're done"
  // message.
  useWaitForConversationEnd({
    isConversationOver,
    isRecording,
    // Match legacy 2s trailing buffer so non-voice (typed) flows don't cut
    // off on the same tick as conversation-over. With TTS, this also covers
    // the brief post-audio pause on the recording.
    trailingBufferMs: 2000,
    waitForTts: project?.isAiVoiceEnabled ?? false,
    onReady: () => {
      if (globalHasEnded) return;
      globalHasEnded = true;
      endRecording();
      globalHasStarted = false;
    },
  });

  if (!isPublished) {
    return (
      <SurveyThemeProvider value={themeValue}>
        <ProjectExpired showHomePageButton showPoweredBy />
      </SurveyThemeProvider>
    );
  }

  if (!mounted) {
    return (
      <SurveyThemeProvider value={themeValue}>
        <div className='flex h-screen w-screen items-center justify-center bg-gray-50' />
      </SurveyThemeProvider>
    );
  }

  if (!isDesktop && !isMobileMode) {
    return (
      <SurveyThemeProvider value={themeValue}>
        <ProjectUsabilityResponseMobileModal />
      </SurveyThemeProvider>
    );
  }

  if (!supportsDisplayMedia()) {
    return (
      <SurveyThemeProvider value={themeValue}>
        <ProjectUsabilityResponseUnsupportedBrowser />
      </SurveyThemeProvider>
    );
  }

  if (showWelcomePrompt && !isRecording && !isPermissionDenied) {
    return (
      <SurveyThemeProvider value={themeValue}>
        <ProjectUsabilityResponseWelcomeModal
          projectId={project.id}
          startRecording={handleStartRecording}
          isLoading={hasRequestedStart || isRequestingPermission}
          showCameraSetup={!isMobileMode}
        />
      </SurveyThemeProvider>
    );
  }

  if (isPermissionDenied) {
    return (
      <SurveyThemeProvider value={themeValue}>
        <ProjectUsabilityResponsePermissionDenied
          permissionError={recordingError || t('permissionRequiredError')}
          requestPermissions={() => {
            globalHasStarted = false;
            globalHasEnded = false;
            window.location.reload();
          }}
        />
      </SurveyThemeProvider>
    );
  }

  return (
    <SurveyThemeProvider value={themeValue}>
      <UsabilityResponseContext
        value={{ project, session, voice, stage, conversationOverText }}
      >
        <div
          data-test='project-usability-response'
          className='mx-auto flex min-h-dvh max-w-640 flex-col justify-between'
        >
          <ProjectUsabilityResponseOverlays />

          <ProjectUsabilityCameraBubble projectId={project.id} />

          <div
            className={cn('flex size-full grow p-4', isSidebarOpen && 'gap-2')}
          >
            <ProjectUsabilityResponseMainFrame />
            <ProjectUsabilityResponseSidebar />
          </div>
        </div>
      </UsabilityResponseContext>
    </SurveyThemeProvider>
  );
}
